import Select, { DataProp } from "@/ui/select";
import Input from "@/ui/input";
import { IconComponent } from "@/ui/icon";
import TokensFiltersModal from "@features/filters-modals/tokens-filters-modal.tsx";
import GradientBorderRadius from "@/ui/gradient-border-radius";
import { useWallet, WalletType } from "@/providers/wallet";
import {
  getTokenScreenerDefaultFiltersBySelection,
  useTokenScreenerFilter,
} from "@providers/token-screener-filter/token-screener-filter-provider.tsx";
import { UnstyledButton } from "@mantine/core";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "@mantine/hooks";
import {
  FiltersSort,
  ScreenerTokensParams,
} from "@/api/token-screener/screener-tokens";
import { getFavouriteTokens, getHistoryTokens } from "@utils/token.ts";
import { useWeb3Auth } from "@web3auth/modal-react-hooks";
import { useBotTokenTraded } from "@api/bot/bot-get-token-traded.ts";

// const timeFrames = ["All", "History", "Trending", "New Pairs", "Positions"];
const timeFrames = ["All", "History", "Trending", "New Pairs"];
export type TokenDropdownValue =
  | "recent"
  | "history"
  | "trending"
  | "all"
  | "positions";
export type TokenGroup = TokenDropdownValue | "star" | "solana" | "ethereum";

const sortFieldMap: Record<string, TokenDropdownValue | undefined> = {
  All: "all",
  History: "history",
  "New Pairs": "recent",
  Positions: "positions",
  Trending: "trending",
};

const getDropdownValues = (isConnected: boolean): DataProp[] => {
  let values = [...timeFrames];
  if (!isConnected) {
    values = values.filter((v) => v !== "Positions");
  }

  const result = values.map((timeFrame) => {
    return {
      label: timeFrame,
      value: sortFieldMap[timeFrame] ?? null,
      component: (
        <div className="text-xs font-semibold w-[8.0625rem]`">{timeFrame}</div>
      ),
    };
  });

  return result as DataProp[];
};

const TokenScreenerHeader = () => {
  const {
    tokenScreenerWallet,
    setTokenScreenerWallet,
    appPubKey,
    currentWallet,
  } = useWallet();

  const { data: tokensTraded } = useBotTokenTraded({
    appPubKey,
    chainId: currentWallet?.botChainId,
    walletAddress: currentWallet?.address,
  });

  const {
    phrase,
    onFiltersUpdate,
    onPhraseUpdate,
    selection,
    setDropdown,
    toggleStarred,
  } = useTokenScreenerFilter();

  const { isConnected } = useWeb3Auth();

  const [dropdownValues, setDropdownValues] = useState<DataProp[]>(
    getDropdownValues(isConnected)
  );

  useEffect(() => {
    setDropdownValues(getDropdownValues(isConnected));
    if (selection.dropdownValue === "positions" && !isConnected) {
      setDropdown("all");
    }
  }, [isConnected, setDropdown]);

  const handleSelectChange = ({ value }: DataProp) => {
    const newVal = value as TokenDropdownValue;
    setDropdown(newVal as TokenDropdownValue);
  };

  // const handleEthereumClick = () => {
  //   setTokenScreenerWallet((prev: WalletType[]) => {
  //     return prev.includes(WalletType.ETH)
  //       ? prev.filter((n) => n !== WalletType.ETH)
  //       : [...prev, WalletType.ETH];
  //   });
  //   toggleParam("select", "ethereum");
  // };

  const handleSolanaClick = () => {
    setTokenScreenerWallet((prev: WalletType[]) => {
      return prev.includes(WalletType.SOL)
        ? prev.filter((n) => n !== WalletType.SOL)
        : [...prev, WalletType.SOL];
    });
    //setShowStarred(false);
    //toggleParam("select", "solana");
    //setDropdown("solana");
  };

  const handleStarClick = () => {
    toggleStarred();
  };

  const arraysEqual = (
    a: string[] | undefined | null,
    b: string[] | undefined | null
  ) => {
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  useEffect(() => {
    onFiltersUpdate((prev: ScreenerTokensParams["filters"]) => {
      const { sort: nullableSort, tokens, ...rest } = prev ?? {};
      const sort = nullableSort as FiltersSort | undefined;

      const selected = selection.showStarred ? "star" : selection.dropdownValue;

      if (selected === "star") {
        const favouriteTokens = getFavouriteTokens();
        if (arraysEqual(tokens, favouriteTokens)) return prev;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { trending, ...restSort } = sort ?? {};
        return {
          ...rest,
          tokens: favouriteTokens,
          sort: { trending: undefined, ...restSort },
        };
      }

      if (selected === "history") {
        const historyTokens = getHistoryTokens();
        if (arraysEqual(tokens, historyTokens)) return prev;

        return {
          ...rest,
          tokens: historyTokens,
        };
      }

      if (selected === "positions") {
        if (arraysEqual(tokens, tokensTraded?.tokens)) return prev;

        return {
          ...rest,
          tokens: tokensTraded?.tokens ?? [],
        };
      }

      const defaultSort =
        getTokenScreenerDefaultFiltersBySelection(selected)?.sort;

      if (JSON.stringify(defaultSort) !== JSON.stringify(sort)) {
        return {
          ...rest,
          sort: defaultSort,
          tokens: undefined,
        } as ScreenerTokensParams["filters"];
      }

      // only update if sort.trending or tokens need to change
      const expectedTrending = selected === "trending" ? true : undefined;

      const needsSortUpdate = sort?.trending !== expectedTrending;
      const needsTokenClear = tokens !== undefined;

      if (needsSortUpdate || needsTokenClear) {
        return {
          ...rest,
          sort: {
            ...sort,
            trending: expectedTrending,
          },
          tokens: undefined,
        };
      }

      return prev;
    });
  }, [onFiltersUpdate, selection]);

  const debouncedPhraseChange = useDebouncedCallback((value) => {
    onPhraseUpdate(value);
  }, 500);

  return (
    <div className="relative w-full flex justify-between items-center px-[0.625rem]">
      <div className="flex justify-center items-center gap-[0.3125rem]">
        <div className="flex justify-center items-center gap-[0.3125rem]">
          <div className="hidden">
            <GradientBorderRadius hover="r-100" radius="circle" gradient="none">
              <UnstyledButton
                // onClick={handleEthereumClick}
                className={clsx(
                  "w-[1.3750rem] h-[1.3750rem] flex justify-center items-center hover:opacity-100",
                  tokenScreenerWallet.includes(WalletType.ETH)
                    ? "opacity-100"
                    : "opacity-50"
                )}
              >
                <IconComponent name="ethereum" className="h-full w-full" />
              </UnstyledButton>
            </GradientBorderRadius>
          </div>
          <GradientBorderRadius hover="none" radius="circle" gradient="none">
            <UnstyledButton
              disabled
              onClick={handleSolanaClick}
              className={clsx(
                "w-[1.3750rem] h-[1.3750rem] flex justify-center items-center hover:opacity-100",
                tokenScreenerWallet.includes(WalletType.SOL)
                  ? "opacity-100"
                  : "opacity-50"
              )}
            >
              <IconComponent
                name="solanaLogo"
                className="h-full w-full cursor-default"
              />
            </UnstyledButton>
          </GradientBorderRadius>
          <GradientBorderRadius
            hover="r-100"
            radius="circle"
            gradient="none"
            background="black"
          >
            <UnstyledButton
              onClick={handleStarClick}
              className={clsx(
                "w-[1.3750rem] h-[1.3750rem] flex justify-center items-center hover:opacity-100",
                "opacity-100"
              )}
            >
              <IconComponent
                name="starEmpty"
                className="w-[1rem] h-[1rem]"
                fill={selection.showStarred ? "white" : "none"}
              />
            </UnstyledButton>
          </GradientBorderRadius>
        </div>
        <div className="flex justify-center items-center gap-[0.3125rem] h-[1.45rem]">
          <Select
            className="z-200"
            key={selection.dropdownValue}
            data={dropdownValues}
            initValue={
              dropdownValues.find((d) => d.value === selection.dropdownValue) ??
              dropdownValues[0]
            }
            onChange={handleSelectChange}
          />
          <div className="h-[1.45rem] w-[25rem] group">
            <Input
              className="font-semibold group placeholder:text-white"
              placeholder="Token"
              defaultValue={String(phrase)}
              onChange={(value) => debouncedPhraseChange(value)}
              rightSide={
                <GradientBorderRadius
                  radius="left"
                  gradient={
                    phrase && phrase !== "" ? "reversed-100" : "default-border"
                  }
                  hover="input-50-right"
                  className="w-[1.375rem] h-full flex justify-center items-center"
                >
                  <IconComponent
                    name="search"
                    className="w-[0.75rem] h-[0.75rem]"
                  />
                </GradientBorderRadius>
              }
            />
          </div>
        </div>
      </div>
      <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
        <div className="text-base leading-[0.625rem] font-semibold">
          Token Screener
        </div>
      </div>
      <div>
        <TokensFiltersModal />
      </div>
    </div>
  );
};

export default TokenScreenerHeader;
